/**
 * Route-path builders — the single source of truth for URLs the app navigates to itself.
 *
 * Only paths built in more than one place live here. `/mission/:conceptId/:setId` is built
 * both by App.jsx (a Home mission card → onStartRecall) and by RecallRoute (the results
 * screen's "Up next" jump), and those two drifting apart is exactly how the missing
 * remount `key` on the /mission route stayed invisible: App.jsx looked untouched by the
 * feature that introduced the second jump. One builder, one grep.
 */

/**
 * The Recall mission screen for one pre-generated question set.
 * Params are interpolated raw — both are opaque backend ids, never user-typed text.
 */
export function missionPath(conceptId, setId) {
  return `/mission/${conceptId}/${setId}`;
}

/** The Notes library. */
export const NOTES_PATH = '/notes';

/** A chapter's overview. The id is opaque (the backend uses the document's meta.id), so it is encoded. */
export function notesChapterPath(chapterId) {
  return `${NOTES_PATH}/${encodeURIComponent(String(chapterId))}`;
}

/** Inverse of the encoding in `notesChapterPath`: wouter hands us the raw path segment. Malformed escapes pass through. */
export function decodeChapterId(raw) {
  try { return decodeURIComponent(String(raw ?? '')); } catch { return String(raw ?? ''); }
}

/** Notebook page n (1-based). */
export function notesPagePath(chapterId, n) {
  return `${notesChapterPath(chapterId)}/page/${n}`;
}

/** A chapter's Exam Tips. */
export function notesExamPath(chapterId) {
  return `${notesChapterPath(chapterId)}/exam-tips`;
}

/** Sidebar nav key → route path. */
const NAV_PATHS = {
  home: '/home',
  roster: '/roster',
  preferences: '/preferences',
  awards: '/awards',
  explore: '/explore',
  notes: '/notes',
};

/**
 * The route behind a Sidebar destination key. Built at every `onNavigate` call site in
 * App.jsx, which previously carried an inline ternary that had to be edited twice whenever a
 * destination was added.
 *
 * An unrecognised key falls back to Home with a warning rather than navigating nowhere — the
 * keys come from `NAV_ITEMS`, so an unknown one is a wiring bug worth surfacing in the console.
 * @param {string} key
 * @returns {string}
 */
export function navPath(key) {
  const path = NAV_PATHS[key];
  if (!path) {
    console.warn(`routes.navPath: unknown nav key "${key}" — falling back to /home`);
    return NAV_PATHS.home;
  }
  return path;
}
